import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from 'lib/supabase';
import { ProfileService } from '../services/profileService';
import { getPasswordResetRedirectTo } from '../shared/auth/passwordResetRedirect';
import { getApiBaseUrl } from '../shared/config';
import {
  LS_ORG_ID,
  LS_PRODUCT_ID,
  LS_SUPPORT_STAFF_UID,
  LS_SUPPORT_MODE,
  LS_SUPPORT_ROLE,
  LS_SUPPORT_ORG_NAME,
  LS_SUPPORT_ORG_SLUG,
  LS_SUPPORT_ORG_DISPLAY_NAME,
  LS_ORIGINAL_ORG_ID,
  LS_SUPPORT_ORG_ID,
} from '../shared/config/storageKeys';
import { apiFetch } from '../shared/apiClient';
import { getEffectiveRole, type Role } from '../shared/permissions';
import { resolveCurrentOrg } from '../shared/auth/resolveCurrentOrg';

export interface Product {
  id: string;
  organization_id: string;
  name: string;
  jira_project_key: string | null;
  is_default: boolean;
  /** Per-product calendar mapping (org Azure AD lives in sharepoint_config) */
  sharepoint_calendar_url?: string | null;
  sharepoint_list_id?: string | null;
  sharepoint_calendar_name?: string | null;
  /** Reports — per-product branding (migration 037). Server returns these on /api/products list. */
  logo_url?: string | null;
  logo_storage_path?: string | null;
  brand_color?: string | null;
  logo_style?: 'transparent' | 'light_plate' | 'dark_plate' | 'auto_contrast';
  system_requirements_md?: string | null;
  release_notes_field_id?: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface ProductMembership {
  product_id: string;
  /**
   * Post-migration 024 this is always 'member'. The field is kept so existing
   * backend responses (which echo `product_members.role`) typecheck.
   */
  role: 'member';
  product_name?: string;
  jira_project_key?: string | null;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  /** User-editable label (falls back to name if unset on older APIs) */
  display_name?: string;
  /** Org-level role from API (owner | administrator | member) */
  role: string;
  /** First-run setup wizard finished (from GET /api/auth/me when column exists) */
  onboarding_completed?: boolean;
  /** True for anonymized demo clones (support "Duplicate as demo"); sync is refused server-side */
  is_demo?: boolean;
  products?: Product[];
  productMemberships?: ProductMembership[];
}

interface Profile {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  organizations: Organization[];
  currentOrg: Organization | null;
  currentProduct: Product | null;
  loading: boolean;
  initialized: boolean;
  /** True only after `GET /api/auth/me` returned OK for this session (not 429/network/etc.). */
  authMeSucceeded: boolean;
  isSupportUser: boolean;
}

/** Derived effective role for current org + product ('owner' | 'administrator' | 'member' | null). */
interface AuthContextType extends AuthState {
  effectiveRole: string | null;
  signUp: (email: string, password: string, name?: string) => Promise<{ error: string | null }>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  /** Re-authenticates with current password, then sets a new password (Supabase). */
  changePassword: (
    currentPassword: string,
    newPassword: string
  ) => Promise<{ error: string | null }>;
  /** Sends Supabase password reset email (user follows link to /auth). */
  sendPasswordResetEmail: (email: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  selectOrganization: (org: Organization) => void;
  selectProduct: (product: Product) => void;
  createOrganization: (name: string) => Promise<{ error: string | null; org?: Organization }>;
  /** Returns organizations from GET /api/auth/me (same fetch as state; no X-Organization-Id header). */
  refreshUser: () => Promise<Organization[]>;
}

const AuthContext = createContext<AuthContextType | null>(null);

/** Written when GET /api/auth/me confirms platform support staff; cleared on sign-out / non-support OK response. */
export const SUPPORT_STAFF_UID_STORAGE_KEY = LS_SUPPORT_STAFF_UID;

function syncSupportStaffUidCache(userId: string | undefined, apiSaysSupport: boolean) {
  try {
    if (typeof sessionStorage === 'undefined') return;
    if (apiSaysSupport && userId) {
      sessionStorage.setItem(SUPPORT_STAFF_UID_STORAGE_KEY, userId);
    } else {
      sessionStorage.removeItem(SUPPORT_STAFF_UID_STORAGE_KEY);
    }
  } catch {
    // ignore quota / private mode
  }
}

function pickProductForOrg(org: Organization | null): Product | null {
  if (!org?.products?.length) return null;
  const saved = localStorage.getItem(LS_PRODUCT_ID);
  const match = org.products.find((p) => p.id === saved);
  if (match) return match;
  const def = org.products.find((p) => p.is_default);
  return def || org.products[0] || null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    profile: null,
    organizations: [],
    currentOrg: null,
    currentProduct: null,
    loading: true,
    initialized: false,
    authMeSucceeded: false,
    isSupportUser: false,
  });

  const fetchUserData = useCallback(async (session: Session | null): Promise<Organization[]> => {
    if (!session) {
      syncSupportStaffUidCache(undefined, false);
      localStorage.removeItem(LS_ORG_ID);
      localStorage.removeItem(LS_PRODUCT_ID);
      setState((s) => ({
        ...s,
        user: null,
        session: null,
        profile: null,
        organizations: [],
        currentOrg: null,
        currentProduct: null,
        loading: false,
        initialized: true,
        authMeSucceeded: false,
        isSupportUser: false,
      }));
      return [];
    }

    try {
      const base = getApiBaseUrl();
      /** No X-Organization-Id: requireAuth must not 403 when localStorage has a stale org UUID */
      const res = await fetch(`${base}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        const apiSupport = data.user?.isSupportUser === true;
        syncSupportStaffUidCache(session.user.id, apiSupport);
        const organizations = (data.organizations ?? []) as Organization[];
        const savedOrgId = localStorage.getItem(LS_ORG_ID);
        const supportMode = localStorage.getItem(LS_SUPPORT_MODE) === 'true';
        let currentOrg = resolveCurrentOrg({
          organizations,
          savedOrgId,
          supportMode,
          supportOrgId: localStorage.getItem(LS_SUPPORT_ORG_ID),
          supportMeta: {
            name: localStorage.getItem(LS_SUPPORT_ORG_NAME),
            slug: localStorage.getItem(LS_SUPPORT_ORG_SLUG),
            display_name: localStorage.getItem(LS_SUPPORT_ORG_DISPLAY_NAME),
            role: localStorage.getItem(LS_SUPPORT_ROLE),
          },
        }) as Organization | null;

        if (currentOrg) {
          localStorage.setItem(LS_ORG_ID, currentOrg.id);
        } else {
          localStorage.removeItem(LS_ORG_ID);
        }

        // Hydrate products for support sessions (virtual org has no GET /auth/me payload).
        if (
          currentOrg &&
          localStorage.getItem(LS_SUPPORT_MODE) === 'true' &&
          localStorage.getItem(LS_ORG_ID) === currentOrg.id &&
          (!currentOrg.products || currentOrg.products.length === 0)
        ) {
          try {
            const pr = await apiFetch('/api/products');
            if (pr.ok) {
              const payload = await pr.json();
              const products = (payload.products ?? []) as Product[];
              currentOrg = { ...currentOrg, products };
            }
          } catch (e) {
            console.warn('[AuthContext] support session: failed to load products', e);
          }
        }

        const currentProduct = pickProductForOrg(currentOrg);
        if (currentProduct) {
          localStorage.setItem(LS_PRODUCT_ID, currentProduct.id);
        } else {
          localStorage.removeItem(LS_PRODUCT_ID);
        }

        setState({
          user: session.user,
          session,
          profile: data.profile,
          organizations,
          currentOrg,
          currentProduct,
          loading: false,
          initialized: true,
          authMeSucceeded: true,
          isSupportUser: apiSupport,
        });
        return organizations;
      }
      setState((s) => ({
        ...s,
        user: session.user,
        session,
        loading: false,
        initialized: true,
        authMeSucceeded: false,
      }));
      return [];
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      setState((s) => ({
        ...s,
        user: session.user,
        session,
        loading: false,
        initialized: true,
        authMeSucceeded: false,
      }));
      return [];
    }
  }, []);

  useEffect(() => {
    if (state.currentOrg?.id) {
      localStorage.setItem(LS_ORG_ID, state.currentOrg.id);
    }
  }, [state.currentOrg?.id]);

  useEffect(() => {
    if (state.currentProduct?.id) {
      localStorage.setItem(LS_PRODUCT_ID, state.currentProduct.id);
    }
  }, [state.currentProduct?.id]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      fetchUserData(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      fetchUserData(session);
    });

    return () => subscription.unsubscribe();
  }, [fetchUserData]);

  const signUp = useCallback(async (email: string, password: string, name?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: name } },
    });
    return { error: error?.message || null };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message || null };
  }, []);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      const email = state.user?.email;
      if (!email) return { error: 'Not signed in' };
      if (newPassword.length < 6) {
        return { error: 'New password must be at least 6 characters' };
      }
      const { error: verifyErr } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword,
      });
      if (verifyErr) {
        return { error: verifyErr.message || 'Current password is incorrect' };
      }
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      return { error: error?.message || null };
    },
    [state.user?.email]
  );

  const sendPasswordResetEmail = useCallback(async (email: string) => {
    const trimmed = email.trim();
    if (!trimmed) return { error: 'Email is required' };
    const supabaseUrl = (process.env.REACT_APP_SUPABASE_URL || '').trim();
    const supabaseAnonKey = (process.env.REACT_APP_SUPABASE_ANON_KEY || '').trim();
    if (!supabaseUrl || !supabaseAnonKey) {
      return {
        error:
          'Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY on the frontend build.',
      };
    }
    const redirectTo = getPasswordResetRedirectTo();
    if (!redirectTo) {
      return {
        error:
          'Could not build password reset link. Open the app in a browser or set REACT_APP_AUTH_REDIRECT_BASE (e.g. https://your-domain.com).',
      };
    }
    const { error } = await supabase.auth.resetPasswordForEmail(trimmed, { redirectTo });
    if (error && process.env.NODE_ENV === 'development') {
      console.error('[sendPasswordResetEmail]', error.message, { redirectTo });
    }
    return { error: error?.message || null };
  }, []);

  const signOut = useCallback(async () => {
    syncSupportStaffUidCache(undefined, false);
    await supabase.auth.signOut();
    localStorage.removeItem(LS_ORG_ID);
    localStorage.removeItem(LS_PRODUCT_ID);
    localStorage.removeItem(LS_SUPPORT_MODE);
    localStorage.removeItem(LS_SUPPORT_ROLE);
    localStorage.removeItem(LS_SUPPORT_ORG_ID);
    localStorage.removeItem(LS_SUPPORT_ORG_NAME);
    localStorage.removeItem(LS_SUPPORT_ORG_SLUG);
    localStorage.removeItem(LS_SUPPORT_ORG_DISPLAY_NAME);
    localStorage.removeItem(LS_ORIGINAL_ORG_ID);
    ProfileService.clearProfile();
  }, []);

  const selectOrganization = useCallback((org: Organization) => {
    if (typeof window !== 'undefined' && localStorage.getItem(LS_SUPPORT_MODE) === 'true') {
      return;
    }
    localStorage.setItem(LS_ORG_ID, org.id);
    const product = pickProductForOrg(org);
    if (product) {
      localStorage.setItem(LS_PRODUCT_ID, product.id);
    } else {
      localStorage.removeItem(LS_PRODUCT_ID);
    }
    setState((s) => ({
      ...s,
      currentOrg: org,
      currentProduct: product,
    }));
  }, []);

  const selectProduct = useCallback((product: Product) => {
    localStorage.setItem(LS_PRODUCT_ID, product.id);
    setState((s) => ({ ...s, currentProduct: product }));
  }, []);

  const createOrganization = useCallback(
    async (name: string) => {
      if (!state.session) return { error: 'Not authenticated' };

      try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/organizations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${state.session.access_token}`,
          },
          body: JSON.stringify({ name }),
        });

        if (!res.ok) {
          const data = await res.json();
          return { error: data.error || 'Failed to create organization' };
        }

        await fetchUserData(state.session);

        return { error: null };
      } catch (err) {
        return { error: 'Network error' };
      }
    },
    [state.session, fetchUserData]
  );

  const refreshUser = useCallback(async (): Promise<Organization[]> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return fetchUserData(session);
  }, [fetchUserData]);

  const effectiveRole = useMemo(() => {
    // Support impersonation must win before membership resolution — otherwise
    // `currentOrg` can still be loading (null), or the support user's real
    // `org.role` in the target org is member/non-member while the banner shows
    // Owner/Administrator. Same normalization as SupportContext.parseStoredSupportRole.
    try {
      if (localStorage.getItem(LS_SUPPORT_MODE) === 'true') {
        const raw = localStorage.getItem(LS_SUPPORT_ROLE);
        const normalized = (raw ?? '').trim().toLowerCase();
        let supportRole: Role;
        if (normalized === 'owner' || normalized === 'administrator' || normalized === 'member') {
          supportRole = normalized;
        } else {
          supportRole = 'administrator';
        }
        return supportRole;
      }
    } catch {
      /* localStorage unavailable */
    }

    const org = state.currentOrg;
    if (!org) return null;

    const orgRole = org.role;
    if (orgRole === 'owner') return 'owner';
    const product = state.currentProduct;
    const isProductMember = Boolean(
      product && org.productMemberships?.some((m) => m.product_id === product.id)
    );
    return getEffectiveRole(orgRole, isProductMember);
  }, [state.currentOrg, state.currentProduct]);

  const contextValue = useMemo<AuthContextType>(
    () => ({
      ...state,
      effectiveRole,
      signUp,
      signIn,
      changePassword,
      sendPasswordResetEmail,
      signOut,
      selectOrganization,
      selectProduct,
      createOrganization,
      refreshUser,
    }),
    [
      state,
      effectiveRole,
      signUp,
      signIn,
      changePassword,
      sendPasswordResetEmail,
      signOut,
      selectOrganization,
      selectProduct,
      createOrganization,
      refreshUser,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
